import { UITheme, UiThemeMap } from '@app/ui';
import typography from '@app/common/assets/styles/typography.module.scss';

const commonColors = {
  font: '#0e0f17e0',
  'neutral-white': '#ffffff',
  'neutral-stroke': '#d5dce9',
  description: '#0e0f1799',
  heading: '#0e0f17ad',
  'secondary-text': '#0e0f1799',
  'secondary-text-2': '#0e0f1740',

  'menu-text': '#4c5459',
  'menu-hover-text': '#4c5459',
  'menu-hover-background': '#0000000f',

  'table-td': '#4c5459',
  'table-tr-hover-background': '#f3f8fd',
  'table-icon': '#d5dce9',
  'table-icon-hover': '#d5dce9',
  'table-th-sort-hover-background': '#f3f8fd',

  'tab-item': '#146094',

  'table-td-sort-active-background': '#ffffff',
  'table-th-background': '#ffffff',
  'table-th-sort-active-background': '#ffffff',

  'pagination-primary-background': '#f2fcf8',
  'pagination-primary-background-hover': '#f2fcf8',

  'form-label': '#5f6b75',
  'form-control-border': '#ced5e0',
  'form-control-text': '#516578',
  'form-control-text-disabled': '#dfe3e7',
  'form-control-background-disabled': '#fafbfc',

  'spinner-text': '#11b4bb',

  'btn-success-ghost-text': '#1f9683',
  'btn-success-ghost-border': '#1f9683',
  'btn-success-ghost-text-hover': '#1fb197',
  'btn-success-ghost-border-hover': '#1fb197',
  'btn-success-background': '#1f9683',
  'btn-success-background-hover': '#1fb197',

  'dndc-background-background': '#1fb197',
  'dndc-event-background': '#1f9683',

  'select-option-active': '#ebf6fccc',
  'select-option-selected': '#384355',
  'select-option-selected-background': '#e6f0fa',

  'collapsible-section-border': 'rgba(31, 177, 151, 0.3)',
  'collapsible-section-background': '#fbfffc',

  'shortcuts-row-background': 'rgba(0,0,0,0.02)',

  'orange-light': '#FFF8ED',
  'risk-matrix-green': '#0D8A5E',
  'risk-matrix-green-background': '#EAFAF3',
  'risk-matrix-amber': '#D4850A',
  'risk-matrix-amber-background': '#FFF8EB',
  'risk-matrix-red': '#DC3545',
  'risk-matrix-red-background': '#FDF0F0',
};

export const defaultTheme: UITheme = {
  typography,
  colors: {
    ...commonColors,
    primary: '#1B3B6C',
    'primary-hover': '#1F323B',
    'primary-active': '#25477D',
    'primary-bg': '#f3f8fd',

    'menu-selected-text': '#1B3B6C',
    'menu-selected-background': '#f3f8fd',
    'menu-selected-bar': '#00a0a6',

    'tab-ink-bar': '#1B3B6C',
    'tab-item-selected': '#1B3B6C',

    'pagination-primary': '#1B3B6C',
    'pagination-primary-hover': '#1F323B',

    link: '#2A80C3',
    'link-hover': '#1560a8',
    'link-active': '#104e85',
    'link-disabled': '#b3cbe3',

    'form-asterisk': '#1B3B6C',

    'typography-description': 'rgba(0,0,0,0.45)',

    info: '#107cc7',
    'info-border': 'rgba(37, 154, 234, 0.5)',
    'info-background': '#edf7fc',

    warning: '#faad14',
    'warning-border': 'rgba(250, 173, 20, 0.5)',
    'warning-background': '#fff6e3',

    success: '#1fb197',
    'success-border': 'rgba(31, 177, 151, 0.5)',
    'success-background': '#f2fcf8',

    error: '#ff4d4f',
    'error-border': 'rgba(255, 77, 79, 0.5)',
    'error-background': '#ffeeee',

    'addition-dark-green': '#1f9683',
    'addition-classic-green': '#1fb197',
    'addition-light-blue': '#92d8ed',
    'addition-dark-blue': '#0419cf',
    'addition-classic-orange': '#faad14',
    'addition-light-orange': 'rgba(255, 150, 0, 0.1)',
    'addition-dark-purple': '#663399',
    'addition-light-pink': '#f549bf',
    'addition-light-grey': '#f0f0f0',
    'addition-dark-grey': '#5f6b75',
    'addition-classic-grey': '#d9d9d9',
    'addition-classic-red': '#ff4d4f',
    'addition-dark-red': '#8e220b',

    'assessment-icon': '#ffab00',
    'encounter-icon': '#00b8d4',
    'appointment-icon': '#77ab59',

    'mod-billing-mismatch': '#e4a628',
    'mod-billing-overdue': '#ec6010',
    'mod-billing-total-paid': '#1fb197',

    'unbilled-encounter-icon': '#faad14',

    'tonometry-right-eye': '#1B3B6C',
    'tonometry-left-eye': '#2A80C3',

    'tonometry-zone-danger': '#F8EEEE',
    'tonometry-zone-warning': '#F7F1E3',
    'tonometry-zone-normal': '#EEF6F1',
    'tonometry-grid': '#D9E3EA',
    'tonometry-axis': '#758294',
  },
};

export const highContrastTheme: UITheme = {
  typography,
  colors: {
    ...commonColors,
    primary: '#0b6d73',
    'primary-hover': '#117e86',
    'primary-active': '#0f5f65',
    'primary-bg': '#e4f7f799',

    'menu-selected-text': '#157c6b',
    'menu-selected-background': '#e4f7f799',
    'menu-selected-bar': '#00595c',

    'tab-ink-bar': '#126f62',
    'tab-item-selected': '#126f62',

    'pagination-primary': '#126f62',
    'pagination-primary-hover': '#126f62',

    link: '#0b6d73',
    'link-hover': '#117e86',
    'link-active': '#0f5f65',
    'link-disabled': '#117e86',

    'form-asterisk': '#0b6d73',

    'typography-description': '#6c757d',

    info: '#0D47A1',
    'info-border': 'rgba(13, 71, 161, 0.5)',
    'info-background': '#E3F2FD',

    warning: '#994D00',
    'warning-border': 'rgba(153, 77, 0, 0.5)',
    'warning-background': '#FFF3E0',

    success: '#1B5E20',
    'success-border': 'rgba(27, 94, 32, 0.5)',
    'success-background': '#E8F5E9',

    error: '#B71C1C',
    'error-border': 'rgba(183, 28, 28, 0.5)',
    'error-background': '#FFEBEE',

    'addition-dark-green': '#186e62',
    'addition-classic-green': '#137966',
    'addition-light-blue': '#3a8ca2',
    'addition-dark-blue': '#0419cf',
    'addition-classic-orange': '#b27a0f',
    'addition-light-orange': '#d17a00',
    'addition-dark-purple': '#4a2c80',
    'addition-light-pink': '#c93696',
    'addition-light-grey': '#d0d0d0',
    'addition-dark-grey': '#4a5060',
    'addition-classic-grey': '#bcbcbc',
    'addition-classic-red': '#c1272d',
    'addition-dark-red': '#8e220b',

    'assessment-icon': '#CC7700',
    'encounter-icon': '#008C9E',
    'appointment-icon': '#598141',

    'mod-billing-mismatch': '#994D00',
    'mod-billing-overdue': '#c1272d',
    'mod-billing-total-paid': '#1B5E20',

    'tonometry-right-eye': '#143CAD',
    'tonometry-left-eye': '#1F6FB2',

    'tonometry-zone-danger': '#F6E8EA',
    'tonometry-zone-warning': '#F1E8D8',
    'tonometry-zone-normal': '#EAF4EE',
    'tonometry-grid': '#CBD8E2',
    'tonometry-axis': '#647284',
  },
};

// uiThemes is a map of all available themes.
// The 'default' theme is guaranteed to exist based on the UiThemeMap type.
export const uiThemes: UiThemeMap = {
  default: defaultTheme,
  highContrast: highContrastTheme,
};
